import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import IntroModal from './shared/IntroModal';
import { INTROS } from './shared/introContent';
import {
  Bot, MessageCircle, MessagesSquare, Image as ImageIcon, Clapperboard, Megaphone, Palette, Shirt,
  Wand2, FileText, Wrench, Music, Smartphone, ChevronDown, Check, ArrowRight, Zap, Hand,
  Languages, Clock, ShieldCheck, Sparkles, Rocket, Gift, Lock, CalendarClock, Menu, X, Wallet, BellRing, Phone,
  Film, Scissors, Play,
} from 'lucide-react';

// ============================================================================
// TeckiA — page d'accueil publique (refonte 3D, 2026-10-16)
// 3D réalisée en CSS (perspective / preserve-3d) + un peu de JS (inclinaison à la souris), sans bibliothèque lourde.
// Contenu : uniquement des fonctions réelles de la plateforme, aucun chiffre ni avis inventé.
// ============================================================================

const PLAY_URL = 'https://play.google.com/store/apps/details?id=io.tdidigital.teckia';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Space+Grotesk:wght@600;700&display=swap');
.tl { --bg:#05060f; --ink:#f4f6ff; --mut:rgba(226,230,255,.62); --teal:#2dd4c8; --vio:#7c6fee; --pur:#a855f7; --pink:#ec4899; --amb:#f59e0b;
  font-family:'DM Sans','Segoe UI',Arial,sans-serif; background:var(--bg); color:var(--ink); min-height:100vh; overflow-x:hidden; position:relative; }
.tl *, .tl *::before, .tl *::after { box-sizing:border-box; }
.tl a { color:inherit; text-decoration:none; }
.tl h1,.tl h2,.tl h3 { font-family:'Space Grotesk','DM Sans',sans-serif; letter-spacing:-.02em; margin:0; }
.tl p { margin:0; }
.tl button { font-family:inherit; cursor:pointer; }
html { scroll-behavior:smooth; }
.tl section[id] { scroll-margin-top:84px; }
.tl :focus-visible { outline:2px solid var(--teal); outline-offset:3px; border-radius:8px; }

/* ── fond : aurore + sol en perspective ───────────────────────── */
.tl-bg { position:fixed; inset:0; z-index:0; pointer-events:none; overflow:hidden; }
.tl-blob { position:absolute; border-radius:50%; filter:blur(90px); opacity:.55; animation:blobDrift 22s ease-in-out infinite; }
.tl-blob.a { width:620px; height:620px; background:radial-gradient(circle,#2dd4c8 0%,transparent 70%); top:-180px; left:-140px; }
.tl-blob.b { width:720px; height:720px; background:radial-gradient(circle,#7c6fee 0%,transparent 70%); top:20%; right:-260px; animation-delay:-7s; }
.tl-blob.c { width:560px; height:560px; background:radial-gradient(circle,#ec4899 0%,transparent 72%); bottom:-200px; left:25%; opacity:.35; animation-delay:-13s; }
@keyframes blobDrift { 0%,100%{transform:translate3d(0,0,0) scale(1)} 50%{transform:translate3d(60px,40px,0) scale(1.12)} }
.tl-floor { position:absolute; left:-20%; right:-20%; bottom:-6%; height:55%; perspective:520px; opacity:.5;
  -webkit-mask-image:linear-gradient(to top,#000 10%,transparent 90%); mask-image:linear-gradient(to top,#000 10%,transparent 90%); }
.tl-floor::before { content:''; position:absolute; inset:0; transform:rotateX(68deg); transform-origin:50% 100%;
  background-image:linear-gradient(rgba(124,111,238,.45) 1px,transparent 1px),linear-gradient(90deg,rgba(124,111,238,.45) 1px,transparent 1px);
  background-size:64px 64px; animation:floorMove 6s linear infinite; }
@keyframes floorMove { to { background-position:0 64px; } }
.tl-stars { position:absolute; inset:0; background-image:radial-gradient(1.5px 1.5px at 20% 30%,rgba(255,255,255,.7),transparent),radial-gradient(1px 1px at 70% 20%,rgba(255,255,255,.6),transparent),radial-gradient(1.2px 1.2px at 40% 70%,rgba(255,255,255,.5),transparent),radial-gradient(1px 1px at 85% 60%,rgba(255,255,255,.55),transparent),radial-gradient(1.4px 1.4px at 10% 80%,rgba(255,255,255,.5),transparent);
  background-size:520px 520px; opacity:.55; }
.tl > .tl-content { position:relative; z-index:1; }

/* ── navigation ───────────────────────────────────────────────── */
.tl-nav { position:sticky; top:0; z-index:50; transition:background .3s, box-shadow .3s, backdrop-filter .3s; }
.tl-nav.solid { background:rgba(5,6,15,.78); backdrop-filter:blur(16px); box-shadow:0 1px 0 rgba(255,255,255,.07), 0 12px 40px rgba(0,0,0,.35); }
.tl-nav-in { max-width:1200px; margin:0 auto; padding:14px 24px; display:flex; align-items:center; justify-content:space-between; gap:16px; }
.tl-logo { display:flex; align-items:center; gap:11px; font-family:'Space Grotesk',sans-serif; font-weight:700; font-size:22px; letter-spacing:-.03em; }
.tl-logo img { width:38px; height:38px; border-radius:11px; object-fit:cover; box-shadow:0 6px 20px rgba(124,111,238,.45); }
.tl-links { display:flex; gap:26px; }
.tl-links a { color:var(--mut); font-size:14px; font-weight:600; transition:color .2s; }
.tl-links a:hover { color:#fff; }
.tl-nav-cta { display:flex; gap:10px; align-items:center; }
.tl-burger { display:none; background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12); color:#fff; width:42px; height:42px; border-radius:12px; align-items:center; justify-content:center; }
.tl-mobile { display:none; }

/* ── boutons 3D ───────────────────────────────────────────────── */
.btn3d { position:relative; display:inline-flex; align-items:center; justify-content:center; gap:10px; font-weight:800; font-size:16px; color:#fff; border:none; border-radius:16px; padding:16px 30px;
  background:linear-gradient(135deg,var(--teal),var(--vio) 55%,var(--pur)); box-shadow:0 6px 0 #3a2f9b, 0 18px 40px rgba(124,111,238,.45), inset 0 1px 0 rgba(255,255,255,.35);
  transition:transform .15s ease, box-shadow .15s ease; transform:translateY(0); }
.btn3d:hover { transform:translateY(-3px); box-shadow:0 9px 0 #3a2f9b, 0 26px 54px rgba(124,111,238,.55), inset 0 1px 0 rgba(255,255,255,.35); }
.btn3d:active { transform:translateY(5px); box-shadow:0 1px 0 #3a2f9b, 0 8px 18px rgba(124,111,238,.4), inset 0 1px 0 rgba(255,255,255,.3); }
.btn3d.sm { padding:10px 18px; font-size:14px; border-radius:12px; box-shadow:0 4px 0 #3a2f9b, 0 12px 26px rgba(124,111,238,.4), inset 0 1px 0 rgba(255,255,255,.3); }
.btnGlass { display:inline-flex; align-items:center; justify-content:center; gap:9px; font-weight:700; font-size:15px; color:#fff; border-radius:16px; padding:15px 24px;
  background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.14); backdrop-filter:blur(10px); transition:all .2s; }
.btnGlass:hover { background:rgba(255,255,255,.12); border-color:rgba(255,255,255,.3); transform:translateY(-2px); }
.btnGlass.sm { padding:9px 16px; font-size:14px; border-radius:12px; }

/* ── typographie ──────────────────────────────────────────────── */
.grad { background:linear-gradient(110deg,var(--teal),var(--vio) 50%,var(--pink)); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }
.eyebrow { display:inline-flex; align-items:center; gap:8px; font-size:12px; font-weight:800; letter-spacing:.16em; text-transform:uppercase; color:var(--teal);
  background:rgba(45,212,200,.1); border:1px solid rgba(45,212,200,.28); padding:8px 16px; border-radius:999px; }
.sec { max-width:1200px; margin:0 auto; padding:96px 24px; }
.sec-head { text-align:center; max-width:760px; margin:0 auto 56px; }
.sec-head h2 { font-size:clamp(30px,4.6vw,52px); line-height:1.08; margin-top:18px; }
.sec-head p { color:var(--mut); font-size:clamp(15px,2vw,18px); line-height:1.7; margin-top:16px; }

/* ── révélation au défilement ─────────────────────────────────── */
.rv { opacity:0; transform:translateY(34px) rotateX(10deg); transform-origin:50% 100%; transition:opacity .8s cubic-bezier(.2,.7,.2,1), transform .8s cubic-bezier(.2,.7,.2,1); }
.rv.in { opacity:1; transform:none; }

/* ── héros ────────────────────────────────────────────────────── */
.hero { max-width:1200px; margin:0 auto; padding:40px 24px 70px; display:grid; grid-template-columns:1.05fr .95fr; gap:30px; align-items:center; min-height:calc(100vh - 80px); }
.hero h1 { font-size:clamp(38px,6.2vw,76px); line-height:1.02; font-weight:700; margin:22px 0 22px; }
.hero-sub { color:var(--mut); font-size:clamp(16px,2.1vw,20px); line-height:1.7; max-width:560px; }
.hero-cta { display:flex; gap:16px; flex-wrap:wrap; margin-top:34px; align-items:center; }
.trust { display:flex; gap:18px; flex-wrap:wrap; margin-top:34px; color:var(--mut); font-size:13.5px; font-weight:600; }
.trust span { display:inline-flex; align-items:center; gap:7px; }
.trust svg { color:var(--teal); }

.scene { position:relative; height:600px; perspective:1500px; }
.stage { position:absolute; inset:0; transform-style:preserve-3d; transform:rotateX(calc(var(--my,0)*-7deg)) rotateY(calc(var(--mx,0)*11deg)); transition:transform .18s ease-out; }
.ph { position:absolute; left:50%; top:50%; width:278px; height:540px; margin:-270px 0 0 -139px; transform-style:preserve-3d; transform:rotateY(-17deg) rotateX(5deg); animation:phFloat 7s ease-in-out infinite; }
@keyframes phFloat { 0%,100%{transform:rotateY(-17deg) rotateX(5deg) translateY(0)} 50%{transform:rotateY(-12deg) rotateX(3deg) translateY(-14px)} }
.ph::before, .ph::after { content:''; position:absolute; inset:0; border-radius:40px; background:#101230; }
.ph::before { transform:translateZ(-8px); } .ph::after { transform:translateZ(-18px); box-shadow:0 60px 110px rgba(0,0,0,.7), 0 0 120px rgba(124,111,238,.35); }
.ph-face { position:absolute; inset:0; border-radius:40px; overflow:hidden; background:linear-gradient(180deg,#0e1030,#090b1f); border:7px solid #1d2048;
  box-shadow:inset 0 0 0 1.5px rgba(255,255,255,.1), inset 0 0 40px rgba(124,111,238,.12); }
.ph-notch { position:absolute; top:9px; left:50%; width:84px; height:22px; margin-left:-42px; background:#05060f; border-radius:14px; z-index:3; }
.ph-head { padding:44px 16px 12px; display:flex; align-items:center; gap:10px; border-bottom:1px solid rgba(255,255,255,.07); background:rgba(255,255,255,.03); }
.ph-ava { width:34px; height:34px; border-radius:50%; background:linear-gradient(135deg,var(--teal),var(--pur)); display:flex; align-items:center; justify-content:center; font-weight:800; font-size:14px; }
.ph-head b { display:block; font-size:13px; } .ph-head small { color:#4ade80; font-size:11px; font-weight:700; }
.ph-body { padding:14px 12px; display:flex; flex-direction:column; gap:9px; }
.bub { max-width:86%; padding:10px 13px; border-radius:16px; font-size:12.5px; line-height:1.5; opacity:0; animation:bubIn .55s cubic-bezier(.2,.8,.2,1) forwards; }
.bub.u { background:rgba(255,255,255,.09); border-bottom-left-radius:4px; align-self:flex-start; color:rgba(255,255,255,.9); }
.bub.b { background:linear-gradient(135deg,var(--teal),var(--vio) 60%,var(--pur)); border-bottom-right-radius:4px; align-self:flex-end; font-weight:600; box-shadow:0 8px 20px rgba(124,111,238,.35); }
@keyframes bubIn { from{opacity:0; transform:translateY(12px) scale(.96)} to{opacity:1; transform:none} }
.typing { display:inline-flex; gap:4px; padding:11px 14px; background:rgba(255,255,255,.09); border-radius:16px; border-bottom-left-radius:4px; align-self:flex-start; opacity:0; animation:bubIn .5s ease 6.6s forwards; }
.typing i { width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,.6); animation:dot 1.2s ease infinite; }
.typing i:nth-child(2){animation-delay:.15s} .typing i:nth-child(3){animation-delay:.3s}
@keyframes dot { 0%,60%,100%{transform:translateY(0);opacity:.4} 30%{transform:translateY(-4px);opacity:1} }

.chip3d { position:absolute; transform-style:preserve-3d; }
.chip3d > div { display:flex; align-items:center; gap:9px; padding:11px 16px; border-radius:16px; font-weight:700; font-size:13.5px; white-space:nowrap;
  background:linear-gradient(145deg,rgba(255,255,255,.14),rgba(255,255,255,.04)); border:1px solid rgba(255,255,255,.2); backdrop-filter:blur(14px);
  box-shadow:0 22px 44px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.3); animation:chipFloat 6s ease-in-out infinite; }
.chip3d .ic { width:30px; height:30px; border-radius:10px; display:flex; align-items:center; justify-content:center; color:#fff; }
@keyframes chipFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }

.coin { position:absolute; width:104px; height:104px; transform-style:preserve-3d; animation:coinSpin 9s linear infinite; }
.coin > i, .coin > b { position:absolute; inset:0; border-radius:50%; }
.coin > i { background:linear-gradient(135deg,#3b2fb0,#6d3fd1); }
.coin > b { background:#0b0d22 url('/teckia-logo.png') center/72% no-repeat; border:3px solid #7c6fee; backface-visibility:hidden; box-shadow:inset 0 0 20px rgba(45,212,200,.3); }
.coin > b.front { transform:translateZ(7px); } .coin > b.back { transform:rotateY(180deg) translateZ(7px); }
@keyframes coinSpin { to { transform:rotateY(360deg); } }
.coin-shadow { position:absolute; width:90px; height:18px; border-radius:50%; background:radial-gradient(ellipse,rgba(124,111,238,.55),transparent 70%); filter:blur(4px); }

/* ── bande défilante ──────────────────────────────────────────── */
.marq { overflow:hidden; border-block:1px solid rgba(255,255,255,.07); background:rgba(255,255,255,.025); padding:18px 0;
  -webkit-mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent); mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent); }
.marq-track { display:flex; gap:14px; width:max-content; animation:marq 42s linear infinite; }
.marq:hover .marq-track { animation-play-state:paused; }
.marq-track span { display:inline-flex; align-items:center; gap:8px; padding:9px 18px; border-radius:999px; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.09); font-size:14px; font-weight:700; color:rgba(255,255,255,.75); }
@keyframes marq { to { transform:translateX(-50%); } }

/* ── chiffres ─────────────────────────────────────────────────── */
.stats { display:grid; grid-template-columns:repeat(4,1fr); gap:18px; perspective:1200px; }
.stat { text-align:center; padding:30px 16px; border-radius:24px; background:linear-gradient(160deg,rgba(255,255,255,.07),rgba(255,255,255,.02)); border:1px solid rgba(255,255,255,.1);
  box-shadow:0 24px 50px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.12); transform:rotateX(8deg); transition:transform .35s; }
.stat:hover { transform:rotateX(0) translateY(-6px); }
.stat b { display:block; font-family:'Space Grotesk',sans-serif; font-size:clamp(34px,5vw,56px); line-height:1; }
.stat span { display:block; color:var(--mut); font-size:13.5px; font-weight:600; margin-top:10px; }

/* ── cartes 3D inclinables ────────────────────────────────────── */
.tilt { position:relative; transform-style:preserve-3d; transform:perspective(1000px) rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg)); transition:transform .14s ease-out; will-change:transform; }
.tilt:hover { transition:transform .06s linear; }
.card { position:relative; border-radius:26px; padding:28px 26px; overflow:hidden; background:linear-gradient(155deg,rgba(255,255,255,.075),rgba(255,255,255,.02)); border:1px solid rgba(255,255,255,.11);
  box-shadow:0 26px 60px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.14); height:100%; }
.card::after { content:''; position:absolute; inset:0; border-radius:inherit; pointer-events:none; opacity:0; transition:opacity .25s;
  background:radial-gradient(420px circle at var(--gx,50%) var(--gy,50%),rgba(255,255,255,.16),transparent 55%); }
.tilt:hover .card::after { opacity:1; }
.card::before { content:''; position:absolute; width:220px; height:220px; right:-70px; top:-80px; border-radius:50%; background:var(--acc,#7c6fee); opacity:.16; filter:blur(40px); }
.pop { transform:translateZ(46px); }
.ico3d { width:60px; height:60px; border-radius:18px; display:flex; align-items:center; justify-content:center; color:#fff; margin-bottom:18px; background:linear-gradient(145deg,var(--acc),color-mix(in srgb,var(--acc) 55%,#000));
  box-shadow:0 14px 28px color-mix(in srgb,var(--acc) 45%,transparent), inset 0 1px 0 rgba(255,255,255,.4), inset 0 -3px 0 rgba(0,0,0,.25); }
.ico3d img { width:100%; height:100%; border-radius:inherit; object-fit:cover; }
.card h3 { font-size:21px; font-weight:700; margin-bottom:9px; }
.card p { color:var(--mut); font-size:14.5px; line-height:1.65; }
.tags { display:flex; gap:7px; flex-wrap:wrap; margin-top:16px; }
.demo-btn { margin-top:18px; }
.tag { font-size:11.5px; font-weight:700; padding:5px 11px; border-radius:999px; background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.1); color:rgba(255,255,255,.8); }
.tag.ok { background:rgba(74,222,128,.12); border-color:rgba(74,222,128,.35); color:#86efac; }
.grid { display:grid; gap:22px; }
.g4 { grid-template-columns:repeat(4,1fr); } .g3 { grid-template-columns:repeat(3,1fr); } .g2 { grid-template-columns:repeat(2,1fr); }
.bento { display:grid; gap:22px; grid-template-columns:repeat(6,1fr); }
.bento > .b-lg { grid-column:span 3; } .bento > .b-md { grid-column:span 2; }

/* ── flux de l'agent ──────────────────────────────────────────── */
.flow { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-top:38px; position:relative; }
.flow::before { content:''; position:absolute; left:8%; right:8%; top:34px; height:3px; background:linear-gradient(90deg,var(--teal),var(--vio),var(--pink)); opacity:.5; border-radius:3px; }
.flow-i { text-align:center; position:relative; }
.flow-i .dot { width:68px; height:68px; margin:0 auto 14px; border-radius:22px; display:flex; align-items:center; justify-content:center; color:#fff; background:linear-gradient(145deg,#1b1f4a,#0f1233);
  border:1px solid rgba(255,255,255,.18); box-shadow:0 12px 0 #090b1f, 0 26px 40px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.25); transform:rotateX(18deg) rotateZ(-6deg); }
.flow-i b { display:block; font-size:15px; margin-bottom:4px; } .flow-i small { color:var(--mut); font-size:13px; line-height:1.5; display:block; }

/* ── étapes ───────────────────────────────────────────────────── */
.steps { display:grid; gap:22px; grid-template-columns:repeat(3,1fr); }
.step { display:flex; gap:22px; align-items:flex-start; }
.num { font-family:'Space Grotesk',sans-serif; font-size:84px; font-weight:700; line-height:.9; color:#6d5fe8; flex:none;
  text-shadow:1px 1px 0 #3b2fb0,2px 2px 0 #3b2fb0,3px 3px 0 #3b2fb0,4px 4px 0 #342aa0,5px 5px 0 #342aa0,6px 6px 0 #2d2590,7px 7px 0 #2d2590,8px 8px 0 #261f80,12px 14px 26px rgba(0,0,0,.55); }

/* ── paiement ─────────────────────────────────────────────────── */
.pay { display:flex; gap:16px; flex-wrap:wrap; justify-content:center; margin-top:34px; }
.pay-logo { display:flex; align-items:center; justify-content:center; width:150px; height:76px; border-radius:20px; background:rgba(255,255,255,.95); box-shadow:0 18px 36px rgba(0,0,0,.4), 0 5px 0 rgba(255,255,255,.35); transition:transform .25s; }
.pay-logo:hover { transform:translateY(-6px) rotateX(10deg); }
.pay-logo img { max-width:112px; max-height:48px; object-fit:contain; }

/* ── appli mobile ─────────────────────────────────────────────── */
.app-box { display:grid; grid-template-columns:1fr 1fr; gap:30px; align-items:center; padding:46px; border-radius:34px; position:relative; overflow:hidden;
  background:linear-gradient(135deg,rgba(45,212,200,.14),rgba(124,111,238,.16) 55%,rgba(236,72,153,.12)); border:1px solid rgba(255,255,255,.15); box-shadow:0 40px 90px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.2); }
.app-art { perspective:1000px; display:flex; justify-content:center; }
.app-art img { width:min(300px,70%); filter:drop-shadow(0 40px 50px rgba(0,0,0,.55)); transform:rotateY(-14deg) rotateX(4deg); animation:mascot 6s ease-in-out infinite; }
@keyframes mascot { 0%,100%{transform:rotateY(-14deg) rotateX(4deg) translateY(0)} 50%{transform:rotateY(10deg) rotateX(2deg) translateY(-16px)} }
.store { display:inline-flex; align-items:center; gap:12px; padding:12px 20px; border-radius:16px; background:#000; border:1px solid rgba(255,255,255,.25); font-weight:700; transition:transform .2s; }
.store:hover { transform:translateY(-3px); } .store small { display:block; font-size:10.5px; font-weight:600; opacity:.7; line-height:1; } .store b { display:block; font-size:17px; }

/* ── FAQ ──────────────────────────────────────────────────────── */
.faq { max-width:820px; margin:0 auto; display:grid; gap:14px; }
.faq-i { border-radius:20px; background:linear-gradient(155deg,rgba(255,255,255,.07),rgba(255,255,255,.02)); border:1px solid rgba(255,255,255,.1); overflow:hidden; box-shadow:0 14px 34px rgba(0,0,0,.28); }
.faq-q { width:100%; background:none; border:none; color:#fff; text-align:left; padding:20px 24px; display:flex; justify-content:space-between; align-items:center; gap:16px; font-size:16.5px; font-weight:700; }
.faq-q svg { flex:none; transition:transform .3s; color:var(--teal); } .faq-i.open .faq-q svg { transform:rotate(180deg); }
.faq-a { display:grid; grid-template-rows:0fr; transition:grid-template-rows .35s ease; } .faq-i.open .faq-a { grid-template-rows:1fr; }
.faq-a > div { overflow:hidden; } .faq-a p { padding:0 24px 22px; color:var(--mut); font-size:15px; line-height:1.7; }

/* ── appel final ──────────────────────────────────────────────── */
.final { text-align:center; padding:74px 30px; border-radius:38px; position:relative; overflow:hidden; perspective:1000px;
  background:radial-gradient(120% 140% at 50% 0%,rgba(124,111,238,.35),rgba(5,6,15,.2) 60%), linear-gradient(160deg,rgba(255,255,255,.08),rgba(255,255,255,.02)); border:1px solid rgba(255,255,255,.14); box-shadow:0 50px 100px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.2); }
.final .big-coin { position:relative; width:130px; height:130px; margin:0 auto 26px; transform-style:preserve-3d; animation:coinSpin 10s linear infinite; }
.final .big-coin > i, .final .big-coin > b { position:absolute; inset:0; border-radius:50%; }
.final .big-coin > i { background:linear-gradient(135deg,#3b2fb0,#6d3fd1); }
.final .big-coin > b { background:#0b0d22 url('/teckia-logo.png') center/72% no-repeat; border:4px solid #7c6fee; backface-visibility:hidden; box-shadow:inset 0 0 28px rgba(45,212,200,.35), 0 0 60px rgba(124,111,238,.5); }
.final .big-coin > b.front { transform:translateZ(9px); } .final .big-coin > b.back { transform:rotateY(180deg) translateZ(9px); }
.final h2 { font-size:clamp(30px,5vw,54px); line-height:1.08; margin-bottom:16px; }
.final p { color:var(--mut); font-size:18px; max-width:560px; margin:0 auto 32px; line-height:1.65; }

/* ── pied de page ─────────────────────────────────────────────── */
.foot { border-top:1px solid rgba(255,255,255,.08); padding:38px 24px; display:flex; flex-wrap:wrap; gap:18px 30px; justify-content:space-between; align-items:center; max-width:1200px; margin:0 auto; }
.foot a { color:var(--mut); font-size:13.5px; font-weight:600; transition:color .2s; } .foot a:hover { color:var(--teal); }
.foot-links { display:flex; gap:22px; flex-wrap:wrap; }

/* ── responsive ───────────────────────────────────────────────── */
@media (min-width:1001px) and (max-width:1220px) {
  .hero { gap:44px; }
  .hero h1 { font-size:clamp(38px,5vw,62px); }
  .chip3d.c-msg { left:8px !important; top:44px !important; } .chip3d.c-sale { left:-4px !important; }
  .chip3d.c-wa { right:-8px !important; } .scene { transform:scale(.92); transform-origin:60% 50%; }
}
@media (max-width:1000px) {
  .hero { grid-template-columns:1fr; text-align:center; min-height:auto; padding-top:20px; }
  .hero-sub { margin:0 auto; } .hero-cta, .trust { justify-content:center; }
  .scene { height:560px; transform:scale(.9); margin-top:10px; }
  .g4 { grid-template-columns:repeat(2,1fr); } .g3 { grid-template-columns:repeat(2,1fr); }
  .bento > .b-lg, .bento > .b-md { grid-column:span 3; }
  .steps { grid-template-columns:1fr; } .app-box { grid-template-columns:1fr; padding:30px; text-align:center; } .app-box .hero-cta { justify-content:center; }
}
@media (max-width:760px) {
  .tl-links, .tl-nav-cta .hide-m { display:none; } .tl-burger { display:inline-flex; }
  .tl-mobile.open { display:flex; flex-direction:column; gap:4px; padding:10px 24px 20px; background:rgba(5,6,15,.95); backdrop-filter:blur(16px); border-bottom:1px solid rgba(255,255,255,.1); }
  .tl-mobile a { padding:13px 4px; font-weight:700; color:var(--mut); border-bottom:1px solid rgba(255,255,255,.06); }
  .sec { padding:70px 20px; }
  .stats { grid-template-columns:repeat(2,1fr); } .g4, .g3, .g2 { grid-template-columns:1fr; }
  .bento > .b-lg, .bento > .b-md { grid-column:span 6; }
  .flow { grid-template-columns:repeat(2,1fr); gap:30px 14px; } .flow::before { display:none; }
  .scene { height:560px; transform:scale(.82); margin-top:-20px; margin-bottom:-50px; } .chip3d.hide-s { display:none; }
  .chip3d > div { padding:8px 12px; font-size:12px; } .chip3d .ic { width:24px; height:24px; border-radius:8px; }
  .chip3d.c-msg { left:2px !important; top:-6px !important; } .chip3d.c-wa { right:2px !important; top:-6px !important; }
  .chip3d.c-sale { left:50% !important; margin-left:-62px; bottom:-14px !important; } .c-coin { left:auto !important; right:-6px; bottom:40px !important; width:76px; height:76px; } .c-coin-sh { display:none; }
  .final { padding:54px 20px; } .num { font-size:64px; } .btn3d { width:100%; } .hero-cta .btnGlass { width:100%; }
}
@media (prefers-reduced-motion:reduce) {
  .tl *, .tl *::before, .tl *::after { animation:none !important; transition:none !important; }
  .rv { opacity:1; transform:none; } .bub, .typing { opacity:1; } .stage { transform:none; }
}
`;

// ── données ────────────────────────────────────────────────────────────────
const MARQUEE = [
  [MessageCircle, 'Messenger'], [MessagesSquare, 'WhatsApp'], [Megaphone, 'Commentaires'], [Zap, 'Ventes détectées'], [BellRing, 'Alertes Telegram'],
  [ImageIcon, 'Images IA'], [Clapperboard, 'Vidéos IA'], [Film, 'Storyboard'], [Shirt, 'Essayage virtuel'], [CalendarClock, 'Publication automatique'],
  [FileText, 'CV Job'], [Scissors, 'Voice Kar'], [Music, 'Chansons IA'], [Languages, 'Malgache natif'],
];

const AGENT = [
  { icon: MessageCircle, acc: '#2dd4c8', title: 'Messenger & WhatsApp', text: "Répond à vos clients 24h/24 en français et en malgache. Il connaît vos produits, vos prix et votre ton, et répond comme vous le feriez.", tags: ['24h/24', 'FR + MG'] },
  { icon: Megaphone, acc: '#7c6fee', title: 'Commentaires de vos publications', text: "Répond publiquement sous vos posts, avec une consigne par publication. Il peut aussi rattraper les anciens commentaires restés sans réponse.", tags: ['Réponses ciblées', 'Rattrapage'] },
  { icon: Zap, acc: '#f59e0b', title: 'Ventes détectées', text: "TeckiA repère les intentions d'achat dans les conversations et vous alerte tout de suite sur Telegram, pour conclure la vente.", tags: ['Alertes Telegram'] },
  { icon: Hand, acc: '#ec4899', title: 'Reprise en main humaine', text: "Vous reprenez la conversation quand vous voulez : l'IA se met en pause sur ce client et ne vous coupe jamais la parole.", tags: ['Vous gardez le contrôle'] },
];

const STUDIOS = [
  { cls: 'b-lg', img: '/icons/video-gen.png', acc: '#8b5cf6', title: 'Vidéo Studio', text: "Créez des vidéos IA à partir d'un texte ou d'une photo, avec plusieurs styles au choix. Le mode storyboard enchaîne vos scènes une à une, puis assemble le tout en un seul film.", tags: ['Texte → vidéo', 'Photo → vidéo', 'Storyboard'] },
  { cls: 'b-lg', img: '/icons/image-gen.png', acc: '#d946ef', demo: 'ia_studio', title: 'Image Studio', text: "Des visuels produits professionnels en quelques secondes, prêts à publier sur vos réseaux : fini les shootings.", tags: ['Visuels produits', 'Prêt à publier'] },
  { cls: 'b-md', img: '/icons/video-ads.png', acc: '#f97316', title: 'Pub Vidéos', text: "Des vidéos publicitaires courtes et percutantes, au format Stories et Reels.", tags: ['Format vertical'] },
  { cls: 'b-md', img: '/icons/marketing-studio.png', acc: '#ec4899', title: 'Marketing Studio', text: "Affiches, mises en scène de vos produits et essayage virtuel de vêtements.", tags: ['Essayage virtuel'] },
  { cls: 'b-md', icon: CalendarClock, acc: '#3b82f6', title: 'CM Studio', text: "Votre community manager IA : il prépare le visuel et la légende, vous validez, il publie sur votre Page.", tags: ['Vous validez', 'Publication auto'] },
];

const TOOLS = [
  { img: '/icons/cv-job.png', acc: '#6366f1', title: 'CV Job', text: "Un CV professionnel généré par l'IA en français, anglais ou malgache. Remplissez le formulaire ou parlez simplement : plusieurs modèles, export PDF.", tags: ['3 CV offerts', 'Mode vocal', 'PDF'] },
  { icon: Scissors, acc: '#f43f5e', title: 'Voice Kar', text: "Séparez la voix et la musique de n'importe quelle vidéo ou audio : vous récupérez deux MP3 propres, la voix seule et l'ambiance.", tags: ['10 gratuits / mois', 'Vidéo ou audio'] },
  { icon: Wrench, acc: '#f59e0b', title: 'TeckiA Utile', text: "Une boîte à outils : extraire le son d'une vidéo en MP3, télécharger des vidéos publiques, et créer ou transformer une chanson avec l'IA.", tags: ['Vidéo → MP3', 'Chansons IA'] },
];

const FLOW = [
  [MessageCircle, 'Le client écrit', 'Messenger, WhatsApp ou commentaire'],
  [Bot, "L'agent répond", 'En français ou en malgache, en quelques secondes'],
  [Zap, 'La vente est repérée', "Intention d'achat détectée"],
  [BellRing, 'Vous êtes alerté', 'Sur Telegram, pour conclure'],
];

const STEPS = [
  ['Créez votre compte', "Inscription en 2 minutes. Demandez la connexion de votre Page Facebook ou de votre WhatsApp : notre équipe s'en occupe pour vous, sans aucune configuration technique."],
  ['Configurez votre agent', 'Vos produits, vos prix, votre ton de réponse : tout se personnalise en quelques minutes depuis votre tableau de bord, sur le web ou sur mobile.'],
  ['Laissez travailler l\'IA', "Messages, commentaires, ventes, visuels : TeckiA s'en occupe pendant que vous gérez votre business. Vous suivez tout et vous reprenez la main quand vous voulez."],
];

const FAQ = [
  ['Faut-il des connaissances techniques ?', "Non. Pour l'agent Facebook et WhatsApp, notre équipe connecte votre Page ou votre numéro et configure l'agent avec vous. Pour les studios et les outils, tout se fait en quelques clics."],
  ["Dans quelles langues répond l'agent ?", "En français et en malgache natif : il comprend aussi le mélange des deux, très courant à Madagascar, et répond dans la langue de votre client."],
  ['Puis-je reprendre la main sur une conversation ?', "Oui, à tout moment. Dès que vous écrivez à un client, l'IA se met en pause sur cette conversation et vous laisse la main."],
  ["L'essai gratuit est-il vraiment gratuit ?", "Oui : 7 jours d'essai sur votre Page Facebook, sans carte bancaire pour démarrer, et sans engagement ensuite."],
  ['Comment payer ?', "Par MVola, Orange Money ou carte bancaire. Les studios (images, vidéos…) fonctionnent avec un solde en Ariary que vous rechargez, et vous ne payez que ce que vous créez."],
  ['Mes données sont-elles protégées ?', "Vos données restent les vôtres et ne servent qu'à faire fonctionner votre service. Le détail est dans notre politique de confidentialité."],
];

// ── hooks / petits composants ──────────────────────────────────────────────
function useReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.rv');
    if (!('IntersectionObserver' in window)) { els.forEach(e => e.classList.add('in')); return undefined; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    els.forEach(e => io.observe(e));
    return () => io.disconnect();
  }, [rootRef]);
}

// Carte qui s'incline vers la souris, avec un reflet lumineux (aucun re-rendu React : on écrit dans le style).
function Tilt({ children, className = '', style, max = 9 }) {
  const ref = useRef(null);
  const move = useCallback((e) => {
    const el = ref.current;
    if (!el || e.pointerType === 'touch') return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
    el.style.setProperty('--ry', `${(px - 0.5) * max * 2}deg`);
    el.style.setProperty('--rx', `${(0.5 - py) * max * 2}deg`);
    el.style.setProperty('--gx', `${px * 100}%`);
    el.style.setProperty('--gy', `${py * 100}%`);
  }, [max]);
  const leave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg');
  }, []);
  return <div ref={ref} className={`tilt ${className}`} style={style} onPointerMove={move} onPointerLeave={leave}>{children}</div>;
}

function Count({ to, suffix = '' }) {
  const ref = useRef(null);
  const [v, setV] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) { setV(to); return undefined; }
    let raf;
    const io = new IntersectionObserver(([en]) => {
      if (!en.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / 1300);
        setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [to]);
  return <b ref={ref} className="grad">{v}{suffix}</b>;
}

const Coin = ({ className = 'coin', style }) => (
  <div className={className} style={style} aria-hidden="true">
    {[-5, -3, -1, 1, 3, 5].map(z => <i key={z} style={{ transform: `translateZ(${z}px)` }} />)}
    <b className="front" /><b className="back" />
  </div>
);

// ── page ───────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const [solid, setSolid] = useState(false);
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState(0);
  const [demo, setDemo] = useState(null);   // clé d'une présentation vidéo (INTROS)

  useReveal(rootRef);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { document.title = 'TeckiA — L\'IA qui répond à vos clients et crée vos contenus'; }, []);

  // parallaxe de la scène du héros : la scène s'oriente légèrement selon la souris
  const onHeroMove = useCallback((e) => {
    const st = stageRef.current;
    if (!st || e.pointerType === 'touch') return;
    const r = e.currentTarget.getBoundingClientRect();
    st.style.setProperty('--mx', ((e.clientX - r.left) / r.width - 0.5) * 2);
    st.style.setProperty('--my', ((e.clientY - r.top) / r.height - 0.5) * 2);
  }, []);
  const onHeroLeave = useCallback(() => {
    const st = stageRef.current;
    if (st) { st.style.setProperty('--mx', 0); st.style.setProperty('--my', 0); }
  }, []);

  const go = (id) => { setMenu(false); const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <div className="tl" ref={rootRef}>
      <style>{CSS}</style>
      <div className="tl-bg" aria-hidden="true">
        <div className="tl-stars" /><div className="tl-blob a" /><div className="tl-blob b" /><div className="tl-blob c" /><div className="tl-floor" />
      </div>

      <div className="tl-content">
        {/* NAV */}
        <header className={`tl-nav ${solid ? 'solid' : ''}`}>
          <div className="tl-nav-in">
            <a href="/" className="tl-logo"><img src="/teckia-logo.png" alt="" />TeckiA</a>
            <nav className="tl-links" aria-label="Navigation principale">
              <a href="#agent">Agent IA</a><a href="#studios">Studios</a><a href="#tools">Outils</a><a href="#how">Comment ça marche</a><a href="#faq">FAQ</a>
            </nav>
            <div className="tl-nav-cta">
              <button className="btnGlass sm hide-m" onClick={() => navigate('/login')}>Se connecter</button>
              <button className="btn3d sm" onClick={() => navigate('/signup')}>Essai gratuit</button>
              <button className="tl-burger" aria-label="Menu" onClick={() => setMenu(m => !m)}>{menu ? <X size={20} /> : <Menu size={20} />}</button>
            </div>
          </div>
          <div className={`tl-mobile ${menu ? 'open' : ''}`}>
            {[['agent', 'Agent IA'], ['studios', 'Studios'], ['tools', 'Outils'], ['how', 'Comment ça marche'], ['faq', 'FAQ']].map(([id, l]) => <a key={id} href={`#${id}`} onClick={(e) => { e.preventDefault(); go(id); }}>{l}</a>)}
            <a href="/login">Se connecter</a>
          </div>
        </header>

        {/* HÉROS */}
        <section className="hero" onPointerMove={onHeroMove} onPointerLeave={onHeroLeave}>
          <div>
            <span className="eyebrow"><Sparkles size={14} /> Agent IA · Facebook · WhatsApp · Madagascar</span>
            <h1>L'IA qui répond à vos clients, <span className="grad">vend et crée</span> pour vous</h1>
            <p className="hero-sub">TeckiA répond à vos messages et commentaires Facebook et WhatsApp en français et en malgache, repère les ventes, et vous offre en plus un studio complet : images, vidéos, publicités, CV, audio…</p>
            <div className="hero-cta">
              <button className="btn3d" onClick={() => navigate('/signup')}><Rocket size={19} /> Essai gratuit 7 jours</button>
              <a href="#how" className="btnGlass" onClick={(e) => { e.preventDefault(); go('how'); }}>Voir comment ça marche <ArrowRight size={17} /></a>
            </div>
            <div className="trust">
              <span><Check size={16} /> Sans carte pour démarrer</span>
              <span><Check size={16} /> MVola, Orange Money, Carte</span>
              <span><Check size={16} /> Géré par notre équipe</span>
            </div>
          </div>

          <div className="scene" aria-hidden="true">
            <div className="stage" ref={stageRef}>
              <div className="ph">
                <div className="ph-face">
                  <div className="ph-notch" />
                  <div className="ph-head"><div className="ph-ava">T</div><div><b>Votre Page Facebook</b><small>● Agent TeckiA actif</small></div></div>
                  <div className="ph-body">
                    <div className="bub u" style={{ animationDelay: '.6s' }}>Salama! Inona ny vidin'ny formation développeur?</div>
                    <div className="bub b" style={{ animationDelay: '2s' }}>Ny vidiny dia 150 000 Ar. Afaka manomboka ianao raha vao vonona 😊</div>
                    <div className="bub u" style={{ animationDelay: '3.6s' }}>Vous livrez à Toamasina ?</div>
                    <div className="bub b" style={{ animationDelay: '5s' }}>Oui, livraison sous 48 h ! Je vous envoie le lien pour commander ✅</div>
                    <div className="typing"><i /><i /><i /></div>
                  </div>
                </div>
              </div>

              <div className="chip3d c-msg" style={{ left: -34, top: 70, transform: 'translateZ(95px)' }}><div style={{ animationDelay: '0s' }}><span className="ic" style={{ background: 'linear-gradient(135deg,#0ea5e9,#6366f1)' }}><MessageCircle size={16} /></span>Messenger</div></div>
              <div className="chip3d c-wa" style={{ right: -26, top: 168, transform: 'translateZ(120px)' }}><div style={{ animationDelay: '-2s' }}><span className="ic" style={{ background: 'linear-gradient(135deg,#22c55e,#15803d)' }}><MessagesSquare size={16} /></span>WhatsApp</div></div>
              <div className="chip3d c-sale" style={{ left: -52, bottom: 150, transform: 'translateZ(140px)' }}><div style={{ animationDelay: '-4s', borderColor: 'rgba(74,222,128,.5)' }}><span className="ic" style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}><Zap size={16} /></span>Vente détectée !</div></div>
              <div className="chip3d hide-s" style={{ right: 6, bottom: 70, transform: 'translateZ(85px)' }}><div style={{ animationDelay: '-1s' }}><span className="ic" style={{ background: 'linear-gradient(135deg,#d946ef,#8b5cf6)' }}><ImageIcon size={16} /></span>Image IA</div></div>
              <div className="chip3d hide-s" style={{ right: 38, top: 8, transform: 'translateZ(65px)' }}><div style={{ animationDelay: '-3s' }}><span className="ic" style={{ background: 'linear-gradient(135deg,#8b5cf6,#ec4899)' }}><Clapperboard size={16} /></span>Vidéo IA</div></div>
              <Coin className="coin c-coin" style={{ left: 18, bottom: -4, transform: 'translateZ(60px)' }} />
              <div className="coin-shadow c-coin-sh" style={{ left: 11, bottom: -26, transform: 'translateZ(20px) rotateX(90deg)' }} />
            </div>
          </div>
        </section>

        {/* BANDE */}
        <div className="marq" aria-hidden="true">
          <div className="marq-track">
            {[...MARQUEE, ...MARQUEE].map(([Icon, label], i) => <span key={i}><Icon size={16} />{label}</span>)}
          </div>
        </div>

        {/* CHIFFRES */}
        <section className="sec" style={{ paddingBottom: 30 }}>
          <div className="stats">
            {[[<b className="grad">24/7</b>, "Disponibilité de l'agent"], [<Count to={2} />, 'Langues natives : FR et MG'], [<Count to={7} suffix=" j" />, "d'essai gratuit"], [<Count to={10} suffix="+" />, 'outils IA dans une seule appli']].map(([n, l], i) => (
              <div key={i} className="stat rv" style={{ transitionDelay: `${i * 90}ms` }}>{n}<span>{l}</span></div>
            ))}
          </div>
        </section>

        {/* AGENT */}
        <section className="sec" id="agent">
          <div className="sec-head rv">
            <span className="eyebrow"><Bot size={14} /> L'agent IA</span>
            <h2>Il répond. Il vend. <span className="grad">Vous gardez la main.</span></h2>
            <p>Votre agent connaît votre business et traite chaque message comme vous le feriez, de jour comme de nuit.</p>
          </div>
          <div className="grid g4">
            {AGENT.map((a, i) => (
              <Tilt key={a.title} className="rv" style={{ transitionDelay: `${i * 90}ms`, '--acc': a.acc }}>
                <div className="card">
                  <div className="ico3d pop"><a.icon size={28} /></div>
                  <h3>{a.title}</h3><p>{a.text}</p>
                  <div className="tags">{a.tags.map(t => <span key={t} className="tag">{t}</span>)}</div>
                </div>
              </Tilt>
            ))}
          </div>
          <div className="flow rv">
            {FLOW.map(([Icon, t, s]) => (
              <div key={t} className="flow-i"><div className="dot"><Icon size={26} /></div><b>{t}</b><small>{s}</small></div>
            ))}
          </div>
        </section>

        {/* STUDIOS */}
        <section className="sec" id="studios">
          <div className="sec-head rv">
            <span className="eyebrow"><Palette size={14} /> Les studios créatifs</span>
            <h2>Vos contenus, <span className="grad">créés par l'IA</span></h2>
            <p>Images, vidéos, publicités, publications : produisez en quelques minutes ce qui demandait des jours. Vous payez à l'usage, en Ariary.</p>
          </div>
          <div className="bento">
            {STUDIOS.map((s, i) => (
              <Tilt key={s.title} className={`rv ${s.cls}`} style={{ transitionDelay: `${i * 80}ms`, '--acc': s.acc }}>
                <div className="card">
                  <div className="ico3d pop">{s.img ? <img src={s.img} alt="" /> : <s.icon size={28} />}</div>
                  <h3>{s.title}</h3><p>{s.text}</p>
                  <div className="tags">{s.tags.map(t => <span key={t} className="tag">{t}</span>)}</div>
                  {s.demo && <button className="btnGlass sm demo-btn" onClick={() => setDemo(s.demo)}><Play size={15} /> Voir la démo en vidéo</button>}
                </div>
              </Tilt>
            ))}
          </div>
        </section>

        {/* OUTILS */}
        <section className="sec" id="tools">
          <div className="sec-head rv">
            <span className="eyebrow"><Wand2 size={14} /> Les outils pratiques</span>
            <h2>Et bien plus, <span className="grad">tout dans une appli</span></h2>
            <p>Des outils du quotidien, simples et rapides — avec une partie gratuite.</p>
          </div>
          <div className="grid g3">
            {TOOLS.map((s, i) => (
              <Tilt key={s.title} className="rv" style={{ transitionDelay: `${i * 90}ms`, '--acc': s.acc }}>
                <div className="card">
                  <div className="ico3d pop">{s.img ? <img src={s.img} alt="" /> : <s.icon size={28} />}</div>
                  <h3>{s.title}</h3><p>{s.text}</p>
                  <div className="tags">{s.tags.map(t => <span key={t} className={`tag ${/gratuit|offert/i.test(t) ? 'ok' : ''}`}>{t}</span>)}</div>
                </div>
              </Tilt>
            ))}
          </div>
          <p className="rv" style={{ textAlign: 'center', color: 'var(--mut)', fontSize: 14, marginTop: 26 }}>
            <Phone size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Besoin d'un réceptionniste vocal ou d'un agent relié à votre site web ? Nous le mettons en place <a href="mailto:contact@tdidigital.io" style={{ color: 'var(--teal)', fontWeight: 700 }}>sur demande</a>.
          </p>
        </section>

        {/* COMMENT ÇA MARCHE */}
        <section className="sec" id="how">
          <div className="sec-head rv">
            <span className="eyebrow"><Clock size={14} /> Comment ça marche</span>
            <h2>Opérationnel <span className="grad">en quelques minutes</span></h2>
            <p>Trois étapes, aucune compétence technique requise.</p>
          </div>
          <div className="steps">
            {STEPS.map(([t, d], i) => (
              <div key={t} className="step rv" style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="num">{i + 1}</div>
                <div><h3 style={{ fontSize: 22, marginBottom: 10 }}>{t}</h3><p style={{ color: 'var(--mut)', lineHeight: 1.7, fontSize: 15 }}>{d}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* PAIEMENT */}
        <section className="sec" id="pricing" style={{ paddingTop: 30 }}>
          <div className="sec-head rv">
            <span className="eyebrow"><Wallet size={14} /> Simple et local</span>
            <h2>Payez comme <span className="grad">vous en avez l'habitude</span></h2>
            <p>Essai gratuit, puis un abonnement sans engagement pour l'agent. Les studios fonctionnent avec un solde en Ariary : vous ne payez que ce que vous créez.</p>
          </div>
          <div className="grid g3" style={{ maxWidth: 980, margin: '0 auto' }}>
            {[[Gift, '#22c55e', "7 jours d'essai", "Sur votre Page Facebook, sans carte bancaire pour démarrer."], [Wallet, '#8b5cf6', "Paiement à l'usage", 'Rechargez votre solde en Ariary et créez images, vidéos et publicités.'], [ShieldCheck, '#2dd4c8', 'Sans engagement', "Vous arrêtez quand vous voulez. Notre équipe vous accompagne tout au long."]].map(([Icon, acc, t, d], i) => (
              <Tilt key={t} className="rv" style={{ transitionDelay: `${i * 90}ms`, '--acc': acc }}>
                <div className="card"><div className="ico3d pop"><Icon size={28} /></div><h3>{t}</h3><p>{d}</p></div>
              </Tilt>
            ))}
          </div>
          <div className="pay rv">
            {[['/mvola.png', 'MVola'], ['/orange.png', 'Orange Money'], ['/card.png', 'Carte bancaire']].map(([src, alt]) => <div key={alt} className="pay-logo"><img src={src} alt={alt} /></div>)}
          </div>
        </section>

        {/* APPLI MOBILE */}
        <section className="sec" style={{ paddingTop: 30 }}>
          <div className="app-box rv">
            <div>
              <span className="eyebrow"><Smartphone size={14} /> Application mobile</span>
              <h2 style={{ fontSize: 'clamp(28px,4vw,44px)', margin: '18px 0 14px', lineHeight: 1.1 }}>TeckiA dans votre poche</h2>
              <p style={{ color: 'var(--mut)', lineHeight: 1.7, fontSize: 16 }}>Suivez vos conversations, lancez vos studios, créez vos CV et recevez les messages importants, où que vous soyez. Disponible sur Android.</p>
              <div className="hero-cta">
                <a className="store" href={PLAY_URL} target="_blank" rel="noreferrer"><Smartphone size={26} /><span><small>Disponible sur</small><b>Google Play</b></span></a>
              </div>
            </div>
            <div className="app-art"><img src="/Teckia-smil.png" alt="Mascotte TeckiA" /></div>
          </div>
        </section>

        {/* FAQ */}
        <section className="sec" id="faq">
          <div className="sec-head rv">
            <span className="eyebrow"><Lock size={14} /> Questions fréquentes</span>
            <h2>Vous avez une question ?</h2>
          </div>
          <div className="faq">
            {FAQ.map(([q, a], i) => (
              <div key={q} className={`faq-i rv ${open === i ? 'open' : ''}`} style={{ transitionDelay: `${i * 60}ms` }}>
                <button className="faq-q" aria-expanded={open === i} onClick={() => setOpen(open === i ? -1 : i)}>{q}<ChevronDown size={20} /></button>
                <div className="faq-a"><div><p>{a}</p></div></div>
              </div>
            ))}
          </div>
        </section>

        {/* APPEL FINAL */}
        <section className="sec" style={{ paddingTop: 20 }}>
          <div className="final rv">
            <Coin className="big-coin" />
            <h2>Essayez TeckiA <span className="grad">gratuitement</span></h2>
            <p>7 jours d'essai sur votre Page Facebook. Aucune carte requise pour démarrer.</p>
            <div className="hero-cta" style={{ justifyContent: 'center', marginTop: 0 }}>
              <button className="btn3d" onClick={() => navigate('/signup')}><Rocket size={19} /> Démarrer mon essai gratuit</button>
              <button className="btnGlass" onClick={() => navigate('/login')}>J'ai déjà un compte</button>
            </div>
          </div>
        </section>

        {demo && INTROS[demo] && (
          <IntroModal intro={INTROS[demo]} ctaLabel="Essayer gratuitement" onClose={() => setDemo(null)} onEnter={() => navigate('/signup')} />
        )}

        {/* PIED */}
        <footer className="foot">
          <a href="/" className="tl-logo" style={{ fontSize: 19 }}><img src="/teckia-logo.png" alt="" style={{ width: 32, height: 32 }} />TeckiA</a>
          <div className="foot-links">
            <a href="/privacy">Politique de confidentialité</a><a href="/terms">CGU</a>
            <a href="mailto:contact@tdidigital.io">Contact</a><a href="https://tdidigital.io" target="_blank" rel="noreferrer">TDI Digital</a>
          </div>
          <span style={{ color: 'rgba(255,255,255,.32)', fontSize: 12.5 }}>© 2026 TDI Digital LLC · Tous droits réservés</span>
        </footer>
      </div>
    </div>
  );
}
